/**
 * UpSurv 2.0 — the thumbnail beside every picture (round 8.1, task 3).
 *
 * Hyde's nine photos are 12.7 MB and Mema's House's 169 are 415 MB, and the
 * shelf drew every one of them full-size on a 168×208 card over one bar of
 * signal. The ruling: a thumbnail — longest side 640 px, JPEG at quality 0.8
 * — is made on the phone the moment the picture is captured, kept beside the
 * original, and sent to the office as a SECOND object under the record's own
 * key with `.thumb.jpg` where the extension was.
 *
 * This module is the pure half: the numbers, the size math and the key. The
 * phone (src/ui/state/makeThumbnail.ts) and the Worker (functions/api/
 * [[route]].ts) both import it, so the two cannot disagree about what a thumb
 * is or where one lives. Nothing here touches a canvas or a bucket.
 */

/** The longest side of a thumbnail, in pixels. */
export const THUMB_MAX_PX = 640;

/** JPEG quality for a thumbnail — enough for a card, a fraction of the original. */
export const THUMB_QUALITY = 0.8;

/** Every thumbnail is a JPEG, whatever the original was. */
export const THUMB_CONTENT_TYPE = 'image/jpeg';

/**
 * The most the office accepts for one thumbnail. A 640-px JPEG at 0.8 is
 * tens of kilobytes; anything near this cap is not a thumbnail, and the
 * Worker answers 413 rather than filing a second full-size picture.
 */
export const MAX_THUMB_BYTES = 512 * 1024;

export interface ThumbSize {
  width: number;
  height: number;
}

/**
 * The size a picture takes when its longest side is brought down to `max`
 * and the other side keeps the ratio. A picture already within the bound is
 * returned as it is — a thumbnail is never upscaled. The short side rounds to
 * a whole pixel, because a canvas has no other kind, and never below one.
 */
export function fitWithin(width: number, height: number, max: number): ThumbSize {
  const longest = Math.max(width, height);
  if (longest <= max) return { width, height };
  const scale = max / longest;
  const short = Math.max(1, Math.round(Math.min(width, height) * scale));
  return width >= height ? { width: max, height: short } : { width: short, height: max };
}

const THUMB_SUFFIX = '.thumb.jpg';

/**
 * The R2 key of a photo's thumbnail: the photo's own key with its final
 * extension replaced by `.thumb.jpg`. Only the last segment is looked at, so
 * a dot in a survey id or a section id is never mistaken for an extension,
 * and a key with no extension simply gains the suffix. A thumb key is its own
 * thumb key, so applying this twice cannot mint a third object.
 */
export function thumbR2Key(r2Key: string): string {
  if (r2Key.endsWith(THUMB_SUFFIX)) return r2Key;
  const slash = r2Key.lastIndexOf('/');
  const dot = r2Key.lastIndexOf('.');
  const stem = dot > slash ? r2Key.slice(0, dot) : r2Key;
  return `${stem}${THUMB_SUFFIX}`;
}

/* ═══════════ the size off the header — final review's fix wave ═══════════ */

/**
 * The oriented pixel size of a picture, read from its file header alone —
 * no decode. `makeThumbnail` (src/ui/state/makeThumbnail.ts) needs the size
 * BEFORE it decodes, to hand `createImageBitmap` a `resizeWidth`/`resizeHeight`
 * so the decoder scales on the way out instead of materialising a 12 MP,
 * ~48 MB bitmap that is then drawn onto a 640-px canvas. The browser offers
 * no cheap size query for a Blob (`createImageBitmap` decodes first; an
 * `<img>` needs a DOM, an object URL and a load event), so this reads the
 * bytes a phone's camera actually writes: JPEG — with the EXIF orientation,
 * because a phone held upright writes a landscape frame and a "rotate 90°"
 * tag, and the browser (`imageOrientation: 'from-image'`) shows the portrait
 * — plus PNG and WebP. Anything else, or a header cut short, is `null`, and
 * the caller decodes at full size as it always did: the parse is a hint, and
 * `fitWithin` on the decoded bitmap still decides the thumb's final size.
 *
 * Pure and in `src/core` so it is proved under Node against hand-built
 * headers (tests/unit/photo-thumb.test.ts).
 */
export interface ImageDimensions {
  width: number;
  height: number;
}

/** How many bytes of a file `imageDimensions` may need: the JPEG frame header can sit behind a large EXIF. */
export const IMAGE_HEADER_BYTES = 256 * 1024;

export function imageDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) return jpegDimensions(bytes);
  if (bytes.length >= 24 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return pngDimensions(bytes);
  }
  if (bytes.length >= 20 && ascii(bytes, 0) === 'RIFF' && ascii(bytes, 8) === 'WEBP') return webpDimensions(bytes);
  return null;
}

function ascii(bytes: Uint8Array, at: number): string {
  return String.fromCharCode(bytes[at] ?? 0, bytes[at + 1] ?? 0, bytes[at + 2] ?? 0, bytes[at + 3] ?? 0);
}

function u16(bytes: Uint8Array, at: number, littleEndian: boolean): number {
  const a = bytes[at] ?? 0;
  const b = bytes[at + 1] ?? 0;
  return littleEndian ? a | (b << 8) : (a << 8) | b;
}

function u32(bytes: Uint8Array, at: number, littleEndian: boolean): number {
  const a = bytes[at] ?? 0;
  const b = bytes[at + 1] ?? 0;
  const c = bytes[at + 2] ?? 0;
  const d = bytes[at + 3] ?? 0;
  return littleEndian ? (a | (b << 8) | (c << 16) | (d << 24)) >>> 0 : ((a << 24) | (b << 16) | (c << 8) | d) >>> 0;
}

/**
 * Walk the JPEG's segments to the first frame header (SOFn) and take its
 * size; take the EXIF orientation from APP1 on the way if there is one. The
 * SOF markers are C0–CF minus C4 (Huffman tables), C8 (reserved) and CC
 * (arithmetic conditioning); RSTn, SOI, EOI and TEM carry no length.
 */
function jpegDimensions(bytes: Uint8Array): ImageDimensions | null {
  let at = 2;
  let quarterTurn = false;
  while (at + 4 <= bytes.length) {
    if (bytes[at] !== 0xff) return null;
    const marker = bytes[at + 1] ?? 0;
    if (marker === 0xff) {
      at += 1; // fill byte
      continue;
    }
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      at += 2;
      continue;
    }
    if (marker === 0xd9 || marker === 0xda) return null; // EOI / SOS before any frame header
    const length = u16(bytes, at + 2, false);
    if (length < 2) return null;
    const isFrame = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isFrame) {
      if (at + 9 > bytes.length) return null;
      const height = u16(bytes, at + 5, false);
      const width = u16(bytes, at + 7, false);
      if (width < 1 || height < 1) return null;
      return quarterTurn ? { width: height, height: width } : { width, height };
    }
    if (marker === 0xe1 && ascii(bytes, at + 4) === 'Exif') {
      quarterTurn = exifQuarterTurn(bytes, at + 10, at + 2 + length) ?? quarterTurn;
    }
    at += 2 + length;
  }
  return null;
}

/**
 * Whether IFD0's orientation tag (0x0112) is one of the four that turn the
 * picture a quarter (5–8), which is when the shown width is the stored
 * height. Undefined when there is no such tag or the TIFF block is not one.
 */
function exifQuarterTurn(bytes: Uint8Array, tiffAt: number, end: number): boolean | undefined {
  if (tiffAt + 8 > end) return undefined;
  const order = ascii(bytes, tiffAt).slice(0, 2);
  const little = order === 'II';
  if (!little && order !== 'MM') return undefined;
  if (u16(bytes, tiffAt + 2, little) !== 0x2a) return undefined;
  const ifd = tiffAt + u32(bytes, tiffAt + 4, little);
  if (ifd + 2 > end) return undefined;
  const entries = u16(bytes, ifd, little);
  for (let i = 0; i < entries; i += 1) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > end) return undefined;
    if (u16(bytes, entry, little) !== 0x0112) continue;
    const orientation = u16(bytes, entry + 8, little);
    return orientation >= 5 && orientation <= 8;
  }
  return undefined;
}

function pngDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (ascii(bytes, 12) !== 'IHDR') return null;
  const width = u32(bytes, 16, false);
  const height = u32(bytes, 20, false);
  return width > 0 && height > 0 ? { width, height } : null;
}

/** The first chunk after the RIFF header says which of WebP's three layouts this is. */
function webpDimensions(bytes: Uint8Array): ImageDimensions | null {
  const chunk = ascii(bytes, 12);
  if (chunk === 'VP8X') {
    if (bytes.length < 30) return null;
    const width = 1 + ((bytes[24] ?? 0) | ((bytes[25] ?? 0) << 8) | ((bytes[26] ?? 0) << 16));
    const height = 1 + ((bytes[27] ?? 0) | ((bytes[28] ?? 0) << 8) | ((bytes[29] ?? 0) << 16));
    return { width, height };
  }
  if (chunk === 'VP8 ') {
    // Frame tag (3), start code 9d 01 2a (3), then 14-bit width and height.
    if (bytes.length < 30) return null;
    if (bytes[23] !== 0x9d || bytes[24] !== 0x01 || bytes[25] !== 0x2a) return null;
    const width = u16(bytes, 26, true) & 0x3fff;
    const height = u16(bytes, 28, true) & 0x3fff;
    return width > 0 && height > 0 ? { width, height } : null;
  }
  if (chunk === 'VP8L') {
    if (bytes.length < 25 || bytes[20] !== 0x2f) return null;
    const bits = u32(bytes, 21, true);
    return { width: 1 + (bits & 0x3fff), height: 1 + ((bits >>> 14) & 0x3fff) };
  }
  return null;
}

/**
 * The one resize option to hand `createImageBitmap`, given the size off the
 * header: the LONG side at `THUMB_MAX_PX`, and only that side, so the browser
 * keeps the ratio itself (the spec computes the other side when one is
 * given) and there is no rounding disagreement between two computations of
 * the short side. Nothing for a picture already within the bound — a
 * thumbnail is never upscaled — and nothing when the size is unknown, which
 * leaves the decoder at full size as before. Should a browser apply the
 * resize before the EXIF turn rather than after, the bitmap comes out with
 * its OTHER side at 640 — still a small bitmap, and `fitWithin` on what
 * actually decoded settles the thumb's final size either way.
 */
export function thumbResizeHint(size: ImageDimensions | null): { resizeWidth?: number; resizeHeight?: number } {
  if (!size) return {};
  if (Math.max(size.width, size.height) <= THUMB_MAX_PX) return {};
  return size.width >= size.height ? { resizeWidth: THUMB_MAX_PX } : { resizeHeight: THUMB_MAX_PX };
}
